import clsx from 'clsx';
import * as React from 'react';

export type IllustrationFrameProps = React.ComponentPropsWithoutRef<'div'> & {
    /** Native width of the illustration in pixels. */
    width: number;
    /** Native height of the illustration in pixels. */
    height: number;
};

/**
 * Note: ONLY intended for empty-state illustrations.
 *
 * Wrapper that lets a fixed-size, mid-fidelity illustration shrink gracefully
 * inside narrower containers. Children render at their native pixel size; the
 * frame uses a CSS container query to compute a uniform `transform: scale()`
 * that maps the native width onto the available width. Everything inside —
 * Tailwind pixel sizes, SVG viewBoxes, absolute positioning — stays in sync
 * because they all shrink as one unit.
 *
 * Above the native width, the `min(1, …)` clamp prevents upscaling so the
 * illustration is pixel-identical to a non-frame layout at full size.
 */
export function IllustrationFrame({
    ref,
    ...props
}: IllustrationFrameProps & { ref?: React.Ref<HTMLDivElement> }) {
    const { width, height, className, children, style, ...rest } = props;
    return (
        <div
            ref={ref}
            {...rest}
            className={clsx('mx-auto w-full', className)}
            style={{ containerType: 'inline-size', maxWidth: width, ...style }}
        >
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: `${width} / ${height}`,
                }}
            >
                <div
                    className="absolute top-0 left-0 origin-top-left"
                    style={{
                        width,
                        height,
                        transform: `scale(min(1, 100cqw / ${width}px))`,
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}
